import React, { useMemo, useCallback, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { Position, useReactFlow, useEdges, Handle } from '@xyflow/react'
import { Layers, Compass, Building2 } from 'lucide-react'
import { App } from 'antd'
import { DismissibleTooltip } from '../../common/DismissibleTooltip'
import styled from 'styled-components'
import type { NodeProps } from '@xyflow/react'
import { CATEGORY_STYLES } from '../../../constants/mindmapConfig'
import { useNodeActions } from '../../../context/nodeActions'
import { useThemeContext } from '../../../context/theme'
import { countHierarchyNodes } from '../../../utils/collapseUtils'
import { formatCollapsedHierarchy } from '../../../utils/nodeFormatter'
import type { SummarizedFlowNode, CollapsedHierarchyNode } from '../../../types/mindmap-components'
import type { NodeCategory } from '@bandos/shared'
import { COLORS, TYPOGRAPHY, FONT_WEIGHT } from '../../../styles/theme'
import { useZoomState } from '../hooks/useZoomState'
import { useScrollIntent } from '../hooks/useScrollIntent'

// Minimized view wrapper - fixed size for stable handle positioning
const MinimizedWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 32px;
  height: 32px;
  z-index: 1;
`

// Circular icon for minimized view
const MinimizedCircle = styled.div.attrs<{ $zoomScaleRaw: number }>((props) => ({
  style: {
    '--zoom-scale-raw': props.$zoomScaleRaw,
  } as React.CSSProperties,
}))`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%) scale(var(--zoom-scale-raw, 1));
  transform-origin: left center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--color-node-bg);
  border: 2px solid ${COLORS.slate500};
  box-shadow: var(--shadow-collapsed);
`

// Count badge for minimized view
const MinimizedCountBadge = styled.div`
  position: absolute;
  left: 50%;
  bottom: 100%;
  transform: translateX(-50%);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  border-radius: 8px;
  background: ${COLORS.slate500};
  color: white;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  box-shadow: var(--shadow-collapsed);
`

// Minimized handles - pointer-events: none to prevent accidental edge creation
// pointer-events: none !important to override ReactFlow's .connectionindicator { pointer-events: all }
const MinimizedTargetHandle = styled(Handle)`
  opacity: 0;
  width: 16px;
  height: 16px;
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none !important;
`

const MinimizedSourceHandle = styled(Handle)`
  opacity: 0;
  width: 16px;
  height: 16px;
  position: absolute;
  right: -8px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none !important;
`

import {
  LeftHandleWrapper,
  HandleLine,
  HandleButton,
  HandleButtonIcon,
  LeftSourceHandle,
} from './styles/HandleStyles'
import {
  NodeWrapper,
  StyledSummarizedContainer,
  CategoryDot,
  LabelChip,
  ScrollableContent,
  HeaderActionsGroup,
  HeaderContainer,
  HeaderLeftSection,
  HeaderTitle,
  HierarchyItemContainer,
  ItemContentRow,
  ItemText,
  LabelChipsContainer,
  ChildrenContainer,
  VerticalLine,
  ChildrenList,
  CategoryIndicatorBar,
  TargetHandle,
  SourceHandle,
} from './styles/SummarizedNodeStyles'
import { MoreOptionsButton } from '../Buttons/MoreOptionsButton'

// Helper to get category color (uses iconColor for category identification)
const getCategoryColor = (category: string): string => {
  const style = CATEGORY_STYLES[category as keyof typeof CATEGORY_STYLES] as
    | { iconColor: string }
    | undefined
  return style ? style.iconColor : '#6b7280'
}

// Recursive component to render hierarchy items
const HierarchyItem = ({ node, depth }: { node: CollapsedHierarchyNode; depth: number }) => {
  const hasChildren = node.children.length > 0
  const hasLabels = node.labels && node.labels.length > 0

  const categoryColor = getCategoryColor(node.category)

  // Format label based on node type
  const getDisplayLabel = () => {
    return node.label || `${node.category} node`
  }

  return (
    <HierarchyItemContainer>
      {/* Item content row */}
      <ItemContentRow>
        <CategoryDot $color={categoryColor} />
        <ItemText>{getDisplayLabel()}</ItemText>
        {/* Label chips to the right of the text */}
        {hasLabels && node.labels && (
          <LabelChipsContainer>
            {node.labels.map((label) => (
              <LabelChip key={label.id} $color={label.color} title={label.label_text}>
                {label.label_text}
              </LabelChip>
            ))}
          </LabelChipsContainer>
        )}
      </ItemContentRow>

      {/* Children with vertical line - pattern from ReportPage.tsx */}
      {hasChildren && (
        <ChildrenContainer>
          {/* Vertical Line */}
          <VerticalLine />

          {/* Children */}
          <ChildrenList>
            {node.children.map((child) => (
              <HierarchyItem key={child.id} node={child} depth={depth + 1} />
            ))}
          </ChildrenList>
        </ChildrenContainer>
      )}
    </HierarchyItemContainer>
  )
}

/**
 * SummarizedNode - displays a collapsed hierarchical view of a node and its descendants.
 * Shows a tree structure with vertical lines indicating parent-child relationships.
 */
// Helper to get parent info based on the original node category
const getParentInfo = (
  category: string
): {
  parentCategory: NodeCategory
  icon: React.ComponentType<{ size: number; color: string; strokeWidth?: number }>
  tooltip: string
} | null => {
  switch (category) {
    case 'need':
      return { parentCategory: 'venture', icon: Building2, tooltip: 'Add venture context' }
    case 'idea':
      return { parentCategory: 'need', icon: Compass, tooltip: 'Add unmet-need context' }
    default:
      return null
  }
}

export const SummarizedNode = React.memo((props: NodeProps<SummarizedFlowNode>) => {
  const { id, data } = props
  const nodeData = data

  // Get callbacks from NodeActionsContext
  const { handleExpandNode, handleAddParent } = useNodeActions()

  // Get theme for icon color
  const { theme } = useThemeContext()
  const headerIconColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.75)' : COLORS.slate500

  // Ref for left handle wrapper
  const leftHandleWrapperRef = useRef<HTMLDivElement>(null)

  // Check if node already has a parent
  const edges = useEdges()

  // Get zoom state - uses optimized hook that only re-renders when zoom crosses threshold
  const { isCollapsed: isMinimized, zoomScale, zoomScaleRaw } = useZoomState()

  // Dynamic scroll intent — only capture wheel when scroll started inside this node
  const { containerRef: scrollIntentRef, shouldCaptureWheel } = useScrollIntent()

  const hasParent = useMemo(() => {
    return edges.some((edge) => edge.target === id)
  }, [edges, id])

  // Count total nodes in the hierarchy (must be before any returns - hooks rule)
  const totalNodes = useMemo(() => {
    return countHierarchyNodes(nodeData.collapsedHierarchy)
  }, [nodeData.collapsedHierarchy])

  // Get the root category color from the original node data
  const rootCategoryColor = getCategoryColor(nodeData.originalNodeData.category)

  // Access React Flow for zoom handling and navigation (must be before any returns - hooks rule)
  const reactFlow = useReactFlow()
  const { setCenter, getNode } = reactFlow

  const { notification: antdNotification } = App.useApp()

  // Get parent info based on original node category
  const originalCategory = nodeData.originalNodeData.category
  const parentInfo = getParentInfo(originalCategory)
  const parentCategoryStyle = parentInfo
    ? CATEGORY_STYLES[parentInfo.parentCategory as keyof typeof CATEGORY_STYLES]
    : null

  // Handle copy to clipboard for MoreOptionsButton (must be before any returns - hooks rule)
  const handleCopy = useCallback(async () => {
    try {
      const formattedText = formatCollapsedHierarchy(nodeData.collapsedHierarchy)
      await navigator.clipboard.writeText(formattedText)
      antdNotification.success({
        message: 'Copied to clipboard',
        description: 'Hierarchy content has been copied to your clipboard.',
        duration: 2,
      } as Parameters<typeof antdNotification.success>[0])
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'mindmap', action: 'copy_collapsed_hierarchy' },
      })
      antdNotification.error({
        message: 'Failed to copy',
        description: 'Could not copy text to clipboard. Please try again.',
        duration: 3,
      } as Parameters<typeof antdNotification.error>[0])
    }
  }, [nodeData.collapsedHierarchy, antdNotification])

  // Handle wheel events: allow scroll but redirect pinch-to-zoom to React Flow
  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      // ctrlKey indicates pinch-to-zoom on trackpad
      if (e.ctrlKey) {
        // Prevent browser zoom
        e.preventDefault()
        // Manually trigger React Flow zoom
        const currentZoom = reactFlow.getZoom()
        const zoomDelta = e.deltaY > 0 ? 0.9 : 1.1 // Zoom out or in
        void reactFlow.zoomTo(currentZoom * zoomDelta, { duration: 0 })
      }
      // For regular scroll (no ctrlKey), let the div scroll naturally
      // Don't stop propagation - the ScrollableContent CSS handles containment
    },
    [reactFlow]
  )

  // Render left handle for adding parent
  const renderLeftHandle = useCallback(() => {
    if (!parentInfo || !parentCategoryStyle) return null
    const IconComponent = parentInfo.icon

    return (
      <DismissibleTooltip title={parentInfo.tooltip} mouseEnterDelay={0.5} placement="left">
        <LeftHandleWrapper
          ref={leftHandleWrapperRef}
          className="nodrag"
          onClick={(e) => {
            e.stopPropagation()
            handleAddParent(id, originalCategory, leftHandleWrapperRef.current ?? undefined)
          }}
        >
          <HandleButton $accentColor={parentCategoryStyle.iconColor}>
            <HandleButtonIcon>
              <IconComponent size={14} color={parentCategoryStyle.iconColor} strokeWidth={2.5} />
            </HandleButtonIcon>
            <LeftSourceHandle type="source" position={Position.Left} id="left-source" />
          </HandleButton>
          <HandleLine $accentColor={parentCategoryStyle.iconColor} $direction="left" />
        </LeftHandleWrapper>
      </DismissibleTooltip>
    )
  }, [id, handleAddParent, parentInfo, parentCategoryStyle, originalCategory])

  // Handle click on minimized view - zoom to node
  const handleMinimizedClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      const node = getNode(id)
      if (node) {
        void setCenter(node.position.x + 16, node.position.y + 16, { zoom: 0.8, duration: 600 })
      }
    },
    [id, getNode, setCenter]
  )

  // Minimized view
  if (isMinimized) {
    return (
      <MinimizedWrapper onClick={handleMinimizedClick}>
        <MinimizedCircle $zoomScaleRaw={zoomScaleRaw}>
          <MinimizedCountBadge>{totalNodes}</MinimizedCountBadge>
          <Layers size={16} color={headerIconColor} />
        </MinimizedCircle>
        <MinimizedTargetHandle
          type="target"
          position={Position.Left}
          id="target"
          isConnectable={false}
        />
        <MinimizedSourceHandle
          type="source"
          position={Position.Right}
          id="source"
          isConnectable={false}
        />
      </MinimizedWrapper>
    )
  }

  return (
    <>
      {/* Handles for edge connections */}
      <TargetHandle type="target" position={Position.Left} id="target" isConnectableStart={false} />
      <SourceHandle type="source" position={Position.Right} id="source" />

      <NodeWrapper $zoomScale={zoomScale}>
        {/* Left handle for adding parent - only show if no parent exists */}
        {!hasParent && renderLeftHandle()}

        <StyledSummarizedContainer>
          {/* Header */}
          <HeaderContainer>
            <HeaderLeftSection>
              <Layers size={16} color={headerIconColor} />
              <HeaderTitle>Collapsed ({totalNodes})</HeaderTitle>
            </HeaderLeftSection>
            <HeaderActionsGroup>
              <MoreOptionsButton
                className="nodrag"
                size={14}
                onCopy={handleCopy}
                onExpand={() => handleExpandNode(id)}
              />
            </HeaderActionsGroup>
          </HeaderContainer>

          {/* Scrollable content area */}
          <ScrollableContent
            ref={scrollIntentRef}
            className={`nodrag${shouldCaptureWheel ? ' nowheel' : ''}`}
            onWheel={handleWheel}
          >
            <HierarchyItem node={nodeData.collapsedHierarchy} depth={0} />
          </ScrollableContent>

          {/* Category indicator bar at bottom */}
          <CategoryIndicatorBar $color={rootCategoryColor} />
        </StyledSummarizedContainer>
      </NodeWrapper>
    </>
  )
})
